import { create } from "zustand";

export const COOKIE_CONSENT_KEY = "cookie_consent";
const COOKIE_MAX_AGE_DAYS = 365;

export type ConsentLevel = "essential" | "all";

/**
 * `consent` is the ONLY source of truth for whether analytics may run.
 *
 * There is deliberately no stored `hasAnalyticsConsent` field. It used to exist,
 * mirroring `consent === "all"`, and was maintained by hand in both `setConsent`
 * and `hydrate`. Any third writer that set `consent` without also clearing the
 * mirror — a withdraw-consent action, a cross-tab `storage` sync, a preferences
 * panel — would leave Clarity and GA4 loading after the visitor withdrew
 * consent, which is a UK PECR / GDPR exposure rather than a cosmetic bug.
 *
 * Derive at the selector instead:
 *
 *   const hasAnalyticsConsent = useCookieConsent((s) => s.consent === "all");
 */
interface CookieConsentState {
  consent: ConsentLevel | null;
  mounted: boolean;
  setConsent: (level: ConsentLevel) => void;
  hydrate: () => void;
}

function getStoredConsent(): ConsentLevel | null {
  if (typeof document === "undefined") return null;
  const raw = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${COOKIE_CONSENT_KEY}=`));
  if (!raw) return null;
  const value = raw.split("=")[1]?.trim();
  if (value === "essential" || value === "all") return value;
  return null;
}

function setConsentCookie(level: ConsentLevel) {
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${COOKIE_CONSENT_KEY}=${level}; path=/; max-age=${maxAge}; SameSite=Lax; Secure`;
}

export const useCookieConsent = create<CookieConsentState>((set) => ({
  consent: null,
  mounted: false,

  setConsent: (level) => {
    setConsentCookie(level);
    set({ consent: level });
  },

  hydrate: () => {
    set({ consent: getStoredConsent(), mounted: true });
  },
}));
